// Grab, carry, throw and escape (§2.6). A carrier is CARRYING with `partner` = its victim (GRABBED,
// `partner` = the carrier), or with `partner` = NO_SLOT while it holds a BOMB entity (`data` = the
// carrier). `syncCarry` keeps both sides consistent after every phase that can change states
// (hazards included) and places what is carried in front of the carrier.
//
// Predict mode (§18.2): grab checks are server-only (the local GRAB_START waits for the outcome),
// escapes are server-only, and the local carrier's throw or drop changes only the local bean.
import { NO_SLOT, TUNE, VOID } from '../constants';
import { EV, emit } from '../events';
import { BS, Cause, EK } from '../ids';
import { MAPS } from '../maps/index';
import { createGroundHit } from '../maps/mapTypes';
import { MODES } from '../modes/index';
import { DEG } from '../math';
import type { Bean, Entity, InputFrame, StepContext, World } from '../types';
import { popFloat } from './balloon';
import { launchBomb } from './bomb';
import { addDizzy, dizzyMult, hitTestPosition } from './combat';
import { dcos, dsin } from './dmath';
import { isGrounded } from './edges';
import { assistedDir } from './input';
import { STATE_TABLE } from './stateTable';
import { isPredicting, isPuppet, isTeammate, setTouch } from './world';

const GRAB_COS = dcos(TUNE.grab.halfArcDeg * DEG);
const hit = createGroundHit();

/** Escape inputs a GRABBED victim needs (§2.6): 10 + floor(dizzy / 20). */
export function escapeNeeded(b: Bean): number {
  return TUNE.grab.escapeBase + TUNE.grab.escapePerDizzy20 * Math.floor(b.dizzy / 20);
}

/** The BOMB entity `slot` is carrying, if any. */
export function carriedBomb(w: World, slot: number): Entity | undefined {
  for (const e of w.ents) if (e.kind === EK.BOMB && e.data === slot) return e;
  return undefined;
}

/** Distance from a carrier's centre to the centre of what it holds (§2.6: carrier.r + held.r + 0.05). */
function holdReach(c: Bean, heldRadius: number): number {
  return c.radius + heldRadius + TUNE.grab.holdOffset;
}

/** Holds a victim in front of the carrier at hold height, moving with it (§2.6). */
function placeVictim(c: Bean, v: Bean): void {
  const r = holdReach(c, v.radius);
  v.pos.x = c.pos.x + dcos(c.facing) * r;
  v.pos.y = c.pos.y + dsin(c.facing) * r;
  v.z = c.z + TUNE.grab.holdZ;
  v.vel.x = c.vel.x;
  v.vel.y = c.vel.y;
  v.vz = c.vz;
  v.groundZ = c.groundZ;
  v.facing = c.facing + Math.PI;
}

function placeBomb(c: Bean, e: Entity): void {
  const r = holdReach(c, TUNE.bomb.bodyRadius);
  e.pos.x = c.pos.x + dcos(c.facing) * r;
  e.pos.y = c.pos.y + dsin(c.facing) * r;
  e.z = c.z + TUNE.grab.holdZ;
  e.vel.x = c.vel.x;
  e.vel.y = c.vel.y;
  e.vz = c.vz;
}

// ---- Grab start and check --------------------------------------------------------------------

/** Grab press (§2.6): NORMAL, grounded and off cooldown enters GRAB_START toward the attack vector. */
export function startGrab(w: World, b: Bean, frame: InputFrame | null): void {
  if (b.cdGrab > 0 || !isGrounded(b) || b.vz > 0) return;
  assistedDir(b.facing, frame ? frame.sx : 0, frame ? frame.sy : 0, b.lungeDir);
  b.state = BS.GRAB_START;
  b.st = 0;
}

/** A bean `a` may grab (§2.14 Grabbable; never big beans, carriers, hangers or invulnerable beans). */
function grabbable(a: Bean, t: Bean): boolean {
  if (t === a || !t.active || t.out || t.invulnTicks > 0 || t.shakeTicks > 0) return false;
  const rule = STATE_TABLE[t.state].grabbable;
  return rule === 'yes' || (rule === 'dz' && Math.abs(t.z - a.z) <= TUNE.balloon.hitDz);
}

/** Range (`a.r + reach + r`), cone (±35° of the grab direction) and height test. */
function inGrabRange(a: Bean, x: number, y: number, z: number, r: number): number {
  const dx = x - a.pos.x;
  const dy = y - a.pos.y;
  const d = Math.hypot(dx, dy);
  if (d > a.radius + TUNE.grab.reach + r || Math.abs(z - a.z) > TUNE.grab.maxDz) return -1;
  if (d > 0 && (dx * a.lungeDir.x + dy * a.lungeDir.y) / d < GRAB_COS) return -1;
  return d;
}

function missGrab(w: World, a: Bean): void {
  a.state = BS.GRAB_MISS;
  a.st = 0;
  a.cdGrab = TUNE.grab.cooldownTicks;
  emit(w, EV.GRAB_MISS, a.slot);
}

function grabBean(w: World, a: Bean, v: Bean): void {
  if (v.state === BS.FLOAT) popFloat(w, v, a.slot);
  a.state = BS.CARRYING;
  a.st = 0;
  a.partner = v.slot;
  v.state = BS.GRABBED;
  v.st = 0;
  v.partner = a.slot;
  v.escape = 0;
  v.floatTicks = 0;
  placeVictim(a, v);
  if (!isTeammate(w, a.slot, v.slot)) setTouch(w, v.slot, a.slot, Cause.THROW);
  emit(w, EV.GRAB, a.slot, v.slot, 0, 0, v.pos.x, v.pos.y);
  MODES[w.cfg.modeId].onHit(w, a.slot, v.slot, Cause.THROW);
}

function grabBomb(w: World, a: Bean, e: Entity): void {
  a.state = BS.CARRYING;
  a.st = 0;
  a.partner = NO_SLOT;
  e.data = a.slot;
  placeBomb(a, e);
  emit(w, EV.GRAB, a.slot, NO_SLOT, e.id, 0, e.pos.x, e.pos.y);
}

/**
 * The check on the last GRAB_START tick (§2.6): the nearest grabbable bean in range (tested at its
 * lag-compensated position), else the nearest loose BOMB in range, else a miss.
 */
function grabCheck(w: World, a: Bean, rewind: number): void {
  let best: Bean | null = null;
  let bestD = Infinity;
  for (const t of w.beans) {
    if (!grabbable(a, t)) continue;
    const p = hitTestPosition(w, t, rewind);
    const d = inGrabRange(a, p.x, p.y, p.z, t.radius);
    if (d >= 0 && d < bestD) {
      best = t;
      bestD = d;
    }
  }
  if (best) {
    grabBean(w, a, best);
    return;
  }
  let bomb: Entity | null = null;
  for (const e of w.ents) {
    if (e.kind !== EK.BOMB || e.data !== NO_SLOT) continue;
    const d = inGrabRange(a, e.pos.x, e.pos.y, e.z, TUNE.bomb.bodyRadius);
    if (d >= 0 && d < bestD) {
      bomb = e;
      bestD = d;
    }
  }
  if (bomb) grabBomb(w, a, bomb);
  else missGrab(w, a);
}

/**
 * Combat phase: grab checks on the last GRAB_START tick. Predict mode (§18.2: grabs are the
 * server's): the local bean keeps reaching in GRAB_START until a snapshot brings the outcome
 * (CARRYING or GRAB_MISS), so a successful grab never flashes a predicted whiff first; only when no
 * outcome has arrived `predictPendingTicks` after the check (extreme lag) is the miss predicted.
 */
export function resolveGrabs(w: World, ctx: StepContext): void {
  const predict = isPredicting(w);
  const check = TUNE.grab.startupTicks - 1;
  for (const a of w.beans) {
    if (!a.active || a.state !== BS.GRAB_START || isPuppet(w, a.slot)) continue;
    if (predict) {
      if (a.st >= check + TUNE.grab.predictPendingTicks) missGrab(w, a);
    } else if (a.st >= check) {
      grabCheck(w, a, ctx.rewindTicks[a.slot] ?? 0);
    }
  }
}

// ---- Throw, escape and drops ----------------------------------------------------------------

function endCarry(c: Bean, cooldown: number): void {
  c.state = BS.NORMAL;
  c.st = 0;
  c.partner = NO_SLOT;
  c.cdGrab = Math.max(c.cdGrab, cooldown);
}

/**
 * Throw (§2.6) along the aim (ax, ay), or facing without one: the victim leaves from the hold point
 * on the throw side at `throwSpeed · dizzyMult · kbDealt · kbMult` (halved for a teammate) with
 * vz 3.5, tumbling, +10 Dizzy and credited (cause THROW). A carried bomb is re-thrown 6 m along the
 * same direction. The carrier returns to NORMAL with the grab cooldown.
 */
export function throwCarried(w: World, c: Bean, ax: number, ay: number): void {
  const l = Math.hypot(ax, ay);
  const ux = l > 0 ? ax / l : dcos(c.facing);
  const uy = l > 0 ? ay / l : dsin(c.facing);
  const victim = c.partner;
  endCarry(c, TUNE.grab.cooldownTicks);
  if (isPredicting(w)) return;
  if (victim === NO_SLOT) {
    const e = carriedBomb(w, c.slot);
    if (!e) return;
    e.data = NO_SLOT;
    e.owner = c.slot;
    const r = holdReach(c, TUNE.bomb.bodyRadius);
    const range = TUNE.bomb.rethrowRange;
    launchBomb(w, e, c.pos.x + ux * r, c.pos.y + uy * r, c.z + TUNE.grab.holdZ, c.pos.x + ux * range, c.pos.y + uy * range);
    emit(w, EV.THROW, c.slot, NO_SLOT, e.id, 0, e.pos.x, e.pos.y);
    return;
  }
  const v = w.beans[victim];
  if (v.state !== BS.GRABBED || v.partner !== c.slot) return;
  const teammate = isTeammate(w, c.slot, v.slot);
  const speed = TUNE.grab.throwSpeed * dizzyMult(v.dizzy) * c.kbDealt * w.rules.kbMult * (teammate ? TUNE.knock.teammateMult : 1);
  const r = holdReach(c, v.radius);
  v.pos.x = c.pos.x + ux * r;
  v.pos.y = c.pos.y + uy * r;
  v.z = c.z + TUNE.grab.holdZ;
  v.groundZ = c.groundZ;
  v.vel.x = ux * speed;
  v.vel.y = uy * speed;
  v.vz = TUNE.grab.throwVz;
  v.state = BS.TUMBLE;
  v.st = 0;
  v.partner = NO_SLOT;
  v.escape = 0;
  v.lastHitTick = w.tick;
  if (!teammate) {
    addDizzy(w, v, TUNE.grab.throwDizzy);
    setTouch(w, v.slot, c.slot, Cause.THROW);
  }
  emit(w, EV.THROW, c.slot, v.slot, 0, Math.round(speed * 10), v.pos.x, v.pos.y);
}

/**
 * Escape mashing (§2.6, server-only): presses accumulate in `escape`; at `escapeNeeded` the victim
 * is set down in front with 5 m/s away from the carrier (no tumble), and the carrier is pushed back
 * at 3 m/s with the longer escape cooldown.
 */
export function countEscape(w: World, v: Bean, presses: number): void {
  if (presses <= 0 || isPredicting(w)) return;
  v.escape = Math.min(255, v.escape + presses);
  const c = v.partner === NO_SLOT ? undefined : w.beans[v.partner];
  if (!c || v.escape < escapeNeeded(v)) return;
  const fx = dcos(c.facing);
  const fy = dsin(c.facing);
  const r = holdReach(c, v.radius);
  v.pos.x = c.pos.x + fx * r;
  v.pos.y = c.pos.y + fy * r;
  v.z = c.z;
  v.groundZ = c.groundZ;
  v.vz = 0;
  v.vel.x = fx * TUNE.grab.escapeVictimSpeed;
  v.vel.y = fy * TUNE.grab.escapeVictimSpeed;
  v.state = BS.NORMAL;
  v.st = 0;
  v.partner = NO_SLOT;
  v.escape = 0;
  c.vel.x = -fx * TUNE.grab.escapeCarrierSpeed;
  c.vel.y = -fy * TUNE.grab.escapeCarrierSpeed;
  endCarry(c, TUNE.grab.escapeCooldownTicks);
  emit(w, EV.ESCAPE, v.slot, c.slot, 0, 0, v.pos.x, v.pos.y);
}

/** How a carrier lets go: hit (victim falls from the hold point with the carrier's velocity), edge (popped inward), place (set down in front). */
export type ReleaseKind = 'hit' | 'edge' | 'place';

/**
 * Releases whatever `c` carries (§2.6 drops) without changing `c.state`: a victim becomes NORMAL
 * ('hit': airborne at the hold point with the carrier's current velocity; 'edge': popped 0.5 m
 * inward from the carrier's edge point; 'place': standing in front, at rest); a bomb drops from
 * the hold point. Sets the grab cooldown. In predict mode only the local carrier changes.
 */
export function releaseCarry(w: World, c: Bean, how: ReleaseKind): void {
  const victim = c.partner;
  c.partner = NO_SLOT;
  c.cdGrab = Math.max(c.cdGrab, TUNE.grab.cooldownTicks);
  if (victim === NO_SLOT) {
    const e = isPredicting(w) ? undefined : carriedBomb(w, c.slot);
    if (e) e.data = NO_SLOT;
    return;
  }
  const v = w.beans[victim];
  if (v.state !== BS.GRABBED || v.partner !== c.slot || isPuppet(w, v.slot)) return;
  placeVictim(c, v);
  v.state = BS.NORMAL;
  v.st = 0;
  v.partner = NO_SLOT;
  v.escape = 0;
  if (how === 'hit') {
    v.vz = 0;
    return;
  }
  v.vel.x = v.vel.y = 0;
  if (how === 'place') {
    v.z = c.z;
    v.vz = 0;
    return;
  }
  MAPS[w.cfg.mapId].nearestGround(w, c.pos.x, c.pos.y, hit);
  v.pos.x = hit.x + hit.nx * TUNE.grab.edgePopInset;
  v.pos.y = hit.y + hit.ny * TUNE.grab.edgePopInset;
  const g = MAPS[w.cfg.mapId].groundAt(w, v.pos.x, v.pos.y);
  v.z = v.groundZ = g === VOID ? c.groundZ : g;
  v.vz = TUNE.grab.edgePopVz;
}

/** A GRABBED victim hit by something (§2.14 "frees"): the carrier lets go and keeps going. */
export function freeFromCarrier(w: World, v: Bean): void {
  const c = w.beans[v.partner];
  v.partner = NO_SLOT;
  v.escape = 0;
  if (c && c.state === BS.CARRYING && c.partner === v.slot) endCarry(c, TUNE.grab.cooldownTicks);
}

/**
 * Keeps carries consistent and places what is carried (call after every phase that can move
 * carriers or change states): a carrier whose victim is no longer its GRABBED victim (or whose
 * bomb is gone) returns to NORMAL; a GRABBED bean whose carrier no longer carries it is freed in
 * place; a bomb whose carrier stopped carrying it drops. Puppets are left alone in predict mode.
 */
export function syncCarry(w: World): void {
  const predict = isPredicting(w);
  for (const c of w.beans) {
    if (!c.active || c.state !== BS.CARRYING) continue;
    const puppet = predict && isPuppet(w, c.slot);
    if (c.partner !== NO_SLOT) {
      const v = w.beans[c.partner];
      // A remote carrier still carries the local victim along in predict mode (it is rendered from prediction).
      if (v.state === BS.GRABBED && v.partner === c.slot) {
        if (!puppet || !isPuppet(w, v.slot)) placeVictim(c, v);
      } else if (!puppet) {
        endCarry(c, TUNE.grab.cooldownTicks);
      }
    } else if (!predict) {
      const e = carriedBomb(w, c.slot);
      if (e) placeBomb(c, e);
      else endCarry(c, TUNE.grab.cooldownTicks);
    }
  }
  for (const v of w.beans) {
    if (!v.active || v.state !== BS.GRABBED || (predict && isPuppet(w, v.slot))) continue;
    const c = v.partner === NO_SLOT ? null : w.beans[v.partner];
    if (c && c.active && c.state === BS.CARRYING && c.partner === v.slot) continue;
    v.state = BS.NORMAL;
    v.st = 0;
    v.partner = NO_SLOT;
    v.escape = 0;
  }
  if (predict) return;
  for (const e of w.ents) {
    if (e.kind !== EK.BOMB || e.data === NO_SLOT) continue;
    const c = w.beans[e.data];
    if (!c || c.state !== BS.CARRYING || c.partner !== NO_SLOT) e.data = NO_SLOT;
  }
}
